import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingDown, RefreshCw } from 'lucide-react';
import { getGtnSummary } from '../../config/api';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => `${Number(v || 0).toFixed(2)}%`;
const iso = (d) => d.toISOString().slice(0, 10);

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 12);
  return { periodStart: iso(start), periodEnd: iso(end) };
}

export default function GrossToNet() {
  const [range, setRange] = useState(defaultRange());
  const [applied, setApplied] = useState(defaultRange());

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['gtn-summary', applied.periodStart, applied.periodEnd],
    queryFn: () => getGtnSummary(applied),
  });

  const gross = Number(data?.grossSales || 0);
  const barPct = (v) => (gross > 0 ? Math.min(100, (Number(v || 0) / gross) * 100) : 0);

  const deductionRows = data ? [
    { label: 'Wholesaler Chargebacks', value: data.actuals.chargebacks, color: 'bg-blue-500' },
    { label: 'GPO Admin Fees', value: data.actuals.adminFees, color: 'bg-purple-500' },
    { label: 'Commercial Rebates', value: data.actuals.commercialRebates, color: 'bg-amber-500' },
    { label: 'Government Rebates', value: data.actuals.governmentRebates, color: 'bg-gray-400', note: 'not yet modelled' },
  ] : [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingDown className="w-6 h-6 text-primary-600" /> Gross-to-Net
        </h1>
        <p className="text-gray-500 mt-1">The gross-to-net bridge — gross sales less chargebacks, GPO admin fees and rebates — composed from your live data.</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-3 no-card-hover">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input type="date" value={range.periodStart} onChange={(e) => setRange((r) => ({ ...r, periodStart: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input type="date" value={range.periodEnd} onChange={(e) => setRange((r) => ({ ...r, periodEnd: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <button onClick={() => { setApplied(range); }} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Apply</button>
        <button onClick={() => refetch()} className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {isLoading && <div className="text-gray-500">Computing gross-to-net…</div>}
      {error && <div className="text-red-600">Failed to load gross-to-net summary.</div>}

      {data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 no-card-hover">
              <div className="text-xs uppercase text-gray-500">Gross Sales</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{money(data.grossSales)}</div>
              <div className="text-xs text-gray-400 mt-1">{data.salesCount} sales lines</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 no-card-hover">
              <div className="text-xs uppercase text-gray-500">Total Deductions</div>
              <div className="text-xl font-bold text-red-600 mt-1">{money(data.actuals.total)}</div>
              <div className="text-xs text-gray-400 mt-1">{pct(data.gtnPercent)} of gross</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 no-card-hover">
              <div className="text-xs uppercase text-gray-500">Net Sales</div>
              <div className="text-xl font-bold text-green-700 mt-1">{money(data.netSales)}</div>
              <div className="text-xs text-gray-400 mt-1">{pct(100 - Number(data.gtnPercent || 0))} of gross</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 no-card-hover">
              <div className="text-xs uppercase text-gray-500">GTN %</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{pct(data.gtnPercent)}</div>
              <div className="text-xs text-gray-400 mt-1">deductions ÷ gross</div>
            </div>
          </div>

          {/* Deduction breakdown */}
          <div className="bg-white rounded-lg shadow p-5 mb-6 no-card-hover">
            <h2 className="font-semibold text-gray-800 mb-4">Deduction Breakdown</h2>
            <div className="space-y-3">
              {deductionRows.map((d) => (
                <div key={d.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{d.label}{d.note && <span className="text-gray-400"> · {d.note}</span>}</span>
                    <span className="text-gray-900 font-medium tabular-nums">{money(d.value)} <span className="text-gray-400">({gross > 0 ? pct((Number(d.value || 0) / gross) * 100) : '—'})</span></span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded">
                    <div className={`h-2 rounded ${d.color}`} style={{ width: `${barPct(d.value)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Estimated (POS) vs Actual */}
          <div className="bg-white rounded-lg shadow p-5 mb-6 no-card-hover">
            <h2 className="font-semibold text-gray-800 mb-1">Estimated (POS accrual) vs Actual (settled)</h2>
            <p className="text-sm text-gray-500 mb-4">Estimated figures are the accruals carried on the sales lines at point of sale; actual figures are settled chargebacks and booked rebate accruals in the period.</p>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase">
                  <th className="py-2 pr-4">Bucket</th>
                  <th className="py-2 pr-4 text-right">Estimated (POS)</th>
                  <th className="py-2 pr-4 text-right">Actual (settled)</th>
                  <th className="py-2 pr-4 text-right">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 tabular-nums">
                <tr>
                  <td className="py-2 pr-4">Chargebacks</td>
                  <td className="py-2 pr-4 text-right">{money(data.estimated.chargebacks)}</td>
                  <td className="py-2 pr-4 text-right">{money(data.actuals.chargebacks)}</td>
                  <td className="py-2 pr-4 text-right">{money(Number(data.estimated.chargebacks || 0) - Number(data.actuals.chargebacks || 0))}</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4">Rebates</td>
                  <td className="py-2 pr-4 text-right">{money(data.estimated.rebates)}</td>
                  <td className="py-2 pr-4 text-right">{money(data.actuals.commercialRebates)}</td>
                  <td className="py-2 pr-4 text-right">{money(Number(data.estimated.rebates || 0) - Number(data.actuals.commercialRebates || 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Monthly trend */}
          {data.months?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-5 no-card-hover overflow-x-auto">
              <h2 className="font-semibold text-gray-800 mb-4">Monthly Trend</h2>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase">
                    <th className="py-2 pr-4">Month</th>
                    <th className="py-2 pr-4 text-right">Gross</th>
                    <th className="py-2 pr-4 text-right">Chargebacks</th>
                    <th className="py-2 pr-4 text-right">Admin Fees</th>
                    <th className="py-2 pr-4 text-right">Rebates</th>
                    <th className="py-2 pr-4 text-right">Net</th>
                    <th className="py-2 pr-4 text-right">GTN %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 tabular-nums">
                  {data.months.map((m) => (
                    <tr key={m.month}>
                      <td className="py-2 pr-4 font-medium text-gray-800">{m.month}</td>
                      <td className="py-2 pr-4 text-right">{money(m.gross)}</td>
                      <td className="py-2 pr-4 text-right">{money(m.chargebacks)}</td>
                      <td className="py-2 pr-4 text-right">{money(m.adminFees)}</td>
                      <td className="py-2 pr-4 text-right">{money(m.rebates)}</td>
                      <td className="py-2 pr-4 text-right font-medium">{money(m.net)}</td>
                      <td className="py-2 pr-4 text-right">{pct(m.gtnPercent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
